export interface PasswordStrength {
  score: number; // 0-4 (0=weak, 4=very strong)
  feedback: string[];
  meets: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    numbers: boolean;
    symbols: boolean;
  };
}

export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSymbols: true,
};

const commonPasswords = [
  'password', '123456', '123456789', '12345678', '12345', '1234567',
  'qwerty', 'abc123', 'password123', 'admin', 'letmein', 'welcome',
  'monkey', '1234567890', 'dragon', 'password1', 'sunshine'
];

export const validatePassword = (password: string): PasswordStrength => {
  const meets = {
    length: password.length >= PASSWORD_REQUIREMENTS.minLength,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    numbers: /\d/.test(password),
    symbols: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const feedback: string[] = [];
  let score = 0;

  // Length check
  if (!meets.length) {
    feedback.push(`Minst ${PASSWORD_REQUIREMENTS.minLength} tecken krävs`);
  } else {
    score += 1;
  }

  // Character variety checks
  if (!meets.uppercase) {
    feedback.push('Minst en stor bokstav krävs');
  } else {
    score += 0.5;
  }

  if (!meets.lowercase) {
    feedback.push('Minst en liten bokstav krävs');
  } else {
    score += 0.5;
  }

  if (!meets.numbers) {
    feedback.push('Minst en siffra krävs');
  } else {
    score += 1;
  }

  if (!meets.symbols) {
    feedback.push('Minst ett specialtecken krävs (!@#$%^&* etc.)');
  } else {
    score += 1;
  }

  // Common password check
  if (commonPasswords.includes(password.toLowerCase())) {
    feedback.push('Välj ett mer unikt lösenord');
    score = Math.max(0, score - 2);
  }

  // Sequential characters check
  if (/123|abc|qwe/i.test(password)) {
    feedback.push('Undvik sekventiella tecken');
    score = Math.max(0, score - 0.5);
  }

  // Repeated characters check
  if (/(.)\1{2,}/.test(password)) {
    feedback.push('Undvik upprepade tecken');
    score = Math.max(0, score - 0.5);
  }

  // Bonus for length
  if (password.length >= 12) {
    score += 0.5;
  }

  // Cap score at 4
  score = Math.min(4, score);

  return {
    score: Math.round(score),
    feedback,
    meets,
  };
};

export const getPasswordStrengthText = (score: number): string => {
  switch (score) {
    case 0:
    case 1:
      return 'Mycket svagt';
    case 2:
      return 'Svagt';
    case 3:
      return 'Bra';
    case 4:
      return 'Mycket starkt';
    default:
      return 'Okänt';
  }
};

export const getPasswordStrengthColor = (score: number): string => {
  switch (score) {
    case 0:
    case 1:
      return 'bg-red-500';
    case 2:
      return 'bg-yellow-500';
    case 3:
      return 'bg-blue-500';
    case 4:
      return 'bg-green-500';
    default:
      return 'bg-gray-300';
  }
};

export const isPasswordStrong = (password: string): boolean => {
  const validation = validatePassword(password);
  return validation.score >= 3 && validation.feedback.length === 0;
};